import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Unauthorized from './pages/Unauthorized';
import Layout from './components/Layout/Layout';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import RequireAdmin from './components/RequireAdmin';

const Users = React.lazy(() => import('./pages/Users'));
const Categories = React.lazy(() => import('./pages/Categories'));
const Employees = React.lazy(() => import('./pages/Employees'));
const Services = React.lazy(() => import('./pages/Services'));
const Orders = React.lazy(() => import('./pages/Orders'));
const Offers = React.lazy(() => import('./pages/Offers'));
const PagesEditor = React.lazy(() => import('./pages/PagesEditor'));
const Roles = React.lazy(() => import('./pages/Roles'));
const Permissions = React.lazy(() => import('./pages/Permissions'));
const AdminManagement = React.lazy(() => import('./pages/Admin/AdminManagement'));
const PartnerDashboard = React.lazy(() => import('./pages/PartnerDashboard'));

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2563eb', // Modern blue
      light: '#3b82f6',
      dark: '#1d4ed8',
    },
    secondary: {
      main: '#f59e0b', // Vibrant orange
      light: '#fbbf24',
      dark: '#d97706',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.25rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '1.875rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.125rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1rem',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
          padding: '8px 16px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          borderRadius: 12,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
      },
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <ToastProvider>
            <Router>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/unauthorized" element={<Unauthorized />} />
                
                {/* Protected routes with Layout */}
                <Route
                  path="/"
                  element={<Layout />}
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="partner-dashboard" element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PartnerDashboard />
                    </React.Suspense>
                  } />
                  
                  {/* Admin-only routes */}
                  <Route path="users" element={
                    <RequireAdmin>
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <Users />
                      </React.Suspense>
                    </RequireAdmin>
                  } />
                  <Route path="roles" element={
                    <RequireAdmin>
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <Roles />
                      </React.Suspense>
                    </RequireAdmin>
                  } />
                  <Route path="permissions" element={
                    <RequireAdmin>
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <Permissions />
                      </React.Suspense>
                    </RequireAdmin>
                  } />
                  <Route path="categories" element={
                    <RequireAdmin>
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <Categories />
                      </React.Suspense>
                    </RequireAdmin>
                  } />
                  <Route path="employees" element={
                    <RequireAdmin>
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <Employees />
                      </React.Suspense>
                    </RequireAdmin>
                  } />
                  <Route path="services" element={
                    <RequireAdmin>
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <Services />
                      </React.Suspense>
                    </RequireAdmin>
                  } />
                  <Route path="orders" element={
                    <RequireAdmin>
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <Orders />
                      </React.Suspense>
                    </RequireAdmin>
                  } />
                  <Route path="offers" element={
                    <RequireAdmin>
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <Offers />
                      </React.Suspense>
                    </RequireAdmin>
                  } />
                  <Route path="pages" element={
                    <RequireAdmin>
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <PagesEditor />
                      </React.Suspense>
                    </RequireAdmin>
                  } />
                  <Route path="admin" element={
                    <RequireAdmin>
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <AdminManagement />
                      </React.Suspense>
                    </RequireAdmin>
                  } />
                </Route>
                
                {/* Catch all route */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Router>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
